import { ServiceType } from '@/shared/api/protocol-ts/api/billing_new/dto_services_new_pb';
import { FeatureFlag } from '@/shared/api/protocol-ts/model/dto_organization_pb';
import { FEATURE_FLAG_ENABLE_SCRIBE_FEATURE } from '@/shared/config';
import { useAppSelector, useIsNonDentalOrganization } from '@/shared/hooks';

import { albumModel } from '@/entities/album';
import { billingModel } from '@/entities/billing';
import { organizationModel } from '@/entities/organization';
import { patientModel } from '@/entities/patient';

import { orderReportSelectors } from '@/features/orderReport';

export const usePatientProfileVisibility = (patientID: string) => {
  const isNonDental = useIsNonDentalOrganization();

  const { CanEditPatient, CanViewStudies, CanRequestReport } = useAppSelector(
    patientModel.selectors.selectPatientYourPermissions(patientID),
  );

  const hasActiveSubscriptionOrPackages = useAppSelector(
    billingModel.selectors.selectHasActiveSubscriptionOrPackages,
  );

  const hasAnyVisibleOrderReportButton = useAppSelector(
    orderReportSelectors.selectHasAnyVisibleOrderReportButton,
  );

  const enablePhotosFeature = useAppSelector(
    organizationModel.selectors.selectFeatureFlag(
      FeatureFlag.FeatureFlag_Show_DentalPhotosWidget,
    ),
  );

  const isUploadPhotoServiceActive = useAppSelector(
    billingModel.selectors.selectIsServiceActiveByType(
      ServiceType.ServiceType_Enable_UploadDentalPhoto,
    ),
  );

  const hasDentalPhotos = useAppSelector(albumModel.selectors.hasDentalPhotos);
  const dentalPhotoCount = useAppSelector(
    (state) => albumModel.selectors.selectAllPhotos(state).length,
  );

  const enableScribeFeature = useAppSelector(
    organizationModel.selectors.selectFeatureFlag(
      FEATURE_FLAG_ENABLE_SCRIBE_FEATURE,
    ),
  );

  const enableAgentChatFeature = useAppSelector(
    organizationModel.selectors.selectFeatureFlag(
      FeatureFlag.FeatureFlag_Enable_AgentChat,
    ),
  );

  const canUploadDentalPhoto =
    enablePhotosFeature || isUploadPhotoServiceActive;

  const shouldShowPhotosWidget =
    !isNonDental && (canUploadDentalPhoto || hasDentalPhotos);

  const showOrderReport =
    CanRequestReport &&
    (hasActiveSubscriptionOrPackages ||
      enableScribeFeature ||
      canUploadDentalPhoto) &&
    hasAnyVisibleOrderReportButton;

  return {
    CanEditPatient,
    CanViewStudies,
    dentalPhotoCount,
    enableAgentChatFeature,
    shouldShowPhotosWidget,
    showOrderReport,
  };
};
